import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  inject,
  input,
  output,
} from '@angular/core';

/**
 * Полоса «смотришь чужими глазами» — одна на оба предпросмотра: организаторский в кабинете и
 * ученический в отдельной вкладке. Позицию (sticky, z) задаёт потребитель классом на хосте:
 * в кабинете полоса стоит под шапкой, в зоне ученика — у самого верха.
 *
 * Кнопка выхода идёт инлайном за текстом, а не флекс-элементом рядом: на телефоне текст переносится
 * на две строки, и «Выйти» обязан остаться в той строке, где текст кончился.
 *
 * Хост публикует свою высоту в `--client-preview-banner-h`: экраны в потоке полоса ужимает сама,
 * а прибитое к окну (`fixed`) её коробки не видит и закрасило бы её собой — такие соседи отступают
 * на эту переменную.
 */
@Component({
  selector: 'app-preview-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block border-b border-acc-line bg-acc-soft-solid px-4 py-2 text-center text-sm leading-4 text-c2',
  },
  template: `<span>{{ text() }}</span
    ><button type="button" class="ml-2 font-semibold text-acc underline-offset-2 hover:underline" (click)="exit.emit()">
      {{ exitLabel() }}
    </button>`,
})
export class PreviewBannerComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  readonly text = input.required<string>();
  readonly exitLabel = input.required<string>();

  readonly exit = output<void>();

  constructor() {
    afterNextRender(() => {
      const element = this.host.nativeElement as HTMLElement;
      const publish = (): void =>
        document.documentElement.style.setProperty('--client-preview-banner-h', `${element.offsetHeight}px`);
      publish();
      const observer = new ResizeObserver(publish);
      observer.observe(element);
      this.destroyRef.onDestroy(() => {
        observer.disconnect();
        document.documentElement.style.removeProperty('--client-preview-banner-h');
      });
    });
  }
}
